<script setup>
import useVuelidate from "@vuelidate/core"
import {required} from "@vuelidate/validators"
import {computed, onMounted, reactive, ref, watch} from "vue"

import InputText from "@/components/form/InputText.vue"
import PaymentMethodRadioGroup from '@/components/form/PaymentMethodRadioGroup.vue'
import FunnelStep from "@/components/FunnelStep.vue"
import FunnelAddressSaved from '@/components/funnelSteps/components/FunnelAddressSaved.vue'
import FunnellAddress from '@/components/funnelSteps/components/FunnellAddress.vue'
import FunnelInvoiceAddress from "@/components/funnelSteps/FunnelInvoiceAddress.vue"
import FunnelPaymentFrequency from "@/components/funnelSteps/FunnelPaymentFrequency.vue"
import FunnelStepLogin from "@/components/funnelSteps/FunnelStepLogin.vue"
import FunnelStepShipping from '@/components/funnelSteps/FunnelStepShipping.vue'
import FunnelStepSummary from "@/components/funnelSteps/FunnelStepSummary.vue"
import { FunnelStepId } from '@/funnelStepId'
import {useAddressStore} from "@/stores/AddressStore"
import {useFormDataStore} from "@/stores/FormDataStore"
import {usePaymentGatewaysStore} from "@/stores/PaymentGatewaysStore"
import { useSettingsStore } from '@/stores/SettingsStore'
import {useSubscriptionsStore} from "@/stores/SubscriptionsStore"
import {useUserStore} from "@/stores/UserStore"
import {useVoucherStore} from "@/stores/VoucherStore"
import {useFunnelSteps} from "@/useFunnelSteps"
import {useHelpers} from "@/useHelpers"

const addressStore = useAddressStore()
const dataStore = useFormDataStore()
const paymentGatewayStore = usePaymentGatewaysStore()
const subscriptionStore = useSubscriptionsStore()
const userStore = useUserStore()
const voucherStore = useVoucherStore()
const settingsStore = useSettingsStore()

const { getInvoiceAddress } = useHelpers()

const isPromoCodeValid = computed(() => {
  if (state.isPromoCodeVisible) {
    return !!voucherStore.voucher
  }
  return true
})

const isAddressStepHidden = !subscriptionStore.hasContentAccess(dataStore.selectedSubscription.code, 'print')

const state = reactive({
  isAlreadyHaveSubscriptionMessageConfirmed: false,
  isOrderInvoiceVisible: false,
  isPromoCodeVisible: false,
  isAddressEditing: false,
  isInvoiceAddressEditing: false,
  isInvoiceAddressSaved: false,
  isShippingAddressSameAsInvoiceAddress: false,
  steps: [
    {
      id: FunnelStepId.SUBSCRIPTION,
      isActive: false,
      isCompleted: true,
    },
    {
      id: FunnelStepId.ACCOUNT,
      isActive: true,
      isCompleted: false,
    },
    {
      id: FunnelStepId.PAYMENT_FREQUENCY,
      isHidden: !subscriptionStore.hasVariants(dataStore.selectedSubscription.id),
      isActive: false,
      isCompleted: false,
    },
    {
      id: FunnelStepId.DELIVERY_METHOD,
      isHidden: isAddressStepHidden || !settingsStore.isPacketaEnabled,
      isActive: false,
      isCompleted: false,
    },
    {
      id: FunnelStepId.ADDRESS,
      isHidden: isAddressStepHidden,
      isActive: false,
      isCompleted: false,
    },
    {
      id: FunnelStepId.PAYMENT_METHOD,
      isActive: false,
      isCompleted: false,
    },
    {
      id: FunnelStepId.SUMMARY,
      isActive: false,
      isCompleted: false,
    },
  ],
  address: {
    id: addressStore.address.id || null,
    first_name: addressStore.address.first_name || '',
    last_name: addressStore.address.last_name || '',
    address: addressStore.address.address || '',
    number: addressStore.address.number || '',
    zip: addressStore.address.zip || '',
    city: addressStore.address.city || '',
    country: addressStore.address.country || window.settings.version,
    phone_number: addressStore.address.phone_number || '',
    company_name: addressStore.address.company_name || '',
  },
  invoiceAddress: getInvoiceAddress(addressStore.invoiceAddress),
  email: addressStore.email,
  promoCode: '',
  paymentGateway: ''
})

const hasVariants = computed(() => subscriptionStore.hasVariants(dataStore.selectedSubscription.id))

watch(() => hasVariants.value, (value) => {
  console.log({hasVariants: value})
})

const $vVoucherCode = useVuelidate({
  promoCode: { required }
}, state)

const {
  handleCompeteStep,
  handleChangeStep,
  assignStepsOrder,
  subscriptionStep,
  accountStep,
  paymentFrequencyStep,
  deliveryMethodStep,
  addressStep,
  paymentMethodStep,
  summaryStep,
} = useFunnelSteps(state.steps)

const handleInvoiceAddressChange = () => {
  state.isInvoiceAddressEditing = true
  state.isInvoiceAddressSaved = false
}

const handlePaymentGatewayUpdate = (resetPromoCode) => {
  if (resetPromoCode) {
    state.isPromoCodeVisible = false
  }

  if (state.isPromoCodeVisible) {
    $vVoucherCode.value.$touch()

    if ($vVoucherCode.value.$invalid || !voucherStore.voucher) {
      return
    }
  }

  handleCompeteStep(FunnelStepId.PAYMENT_METHOD)
}
const handleUsePromoCode = () => {
  voucherStore.validateVoucher(state.promoCode, dataStore.selectedSubscriptionWithVariant.code)
}

const handleFrequencyStepChange = () => {
  voucherStore.resetVoucher()
  handleChangeStep(FunnelStepId.PAYMENT_FREQUENCY)
}

const funnelAddress = ref(null)
const invoiceAddress = ref(null)

onMounted(() => {
  assignStepsOrder(state.steps)

  if (addressStore.email) {
    handleCompeteStep(FunnelStepId.ACCOUNT)
  }

  if (addressStore.hasAddress && funnelAddress.value && funnelAddress.value.isValid()) {
    handleCompeteStep(FunnelStepId.ADDRESS)
  } else {
    state.isAddressEditing = true
  }

  state.paymentGateway = paymentGatewayStore.first.code

  dataStore.setAddress(state.address)
  dataStore.setSendInvoice(state.isOrderInvoiceVisible)
  dataStore.setInvoiceAddress(state.invoiceAddress)
})

watch(
  () => paymentGatewayStore.first,
  (gateway) => {
    state.paymentGateway = gateway.code
  }
)

watch(
  () => state.paymentGateway,
  (value) => {
    return dataStore.setSelectedPaymentGateway(value)
  }
)

watch(
  () => state.isOrderInvoiceVisible,
  (visible) => {
    dataStore.setSendInvoice(visible)

    if (visible && addressStore.hasInvoiceAddress && !invoiceAddress.value.isValid()) {
      state.isInvoiceAddressEditing = true
    } else if (!visible) {
      state.isInvoiceAddressEditing = false
    }
  }
)

watch(
  () => state.address,
  (newAddress) => {
    dataStore.setAddress(newAddress)
    addressStore.addressDraft = newAddress
  },
  { deep: true }
)

watch(
  () => state.invoiceAddress,
  (newInvoiceAddress) => {
    dataStore.setInvoiceAddress(newInvoiceAddress)
    addressStore.invoiceAddressDraft = newInvoiceAddress
  },
  { deep: true }
)

watch(
  () => state.isShippingAddressSameAsInvoiceAddress,
  (isSame) => {
    state.invoiceAddress = getInvoiceAddress(isSame ? { ...state.address, id: null } : addressStore.invoiceAddress)
    state.isInvoiceAddressSaved = false
  }
)

watch(
  () => voucherStore.freeVoucher,
  (free) => {
    state.paymentGateway = free
      ? paymentGatewayStore.freeGatewayCode
      : paymentGatewayStore.first.code
  },
)

const addressEditMode = computed(() => !addressStore.hasAddress || state.isAddressEditing)

</script>

<template>

  <div
    v-if="dataStore.selectedSubscription.activeSubscription && !state.isAlreadyHaveSubscriptionMessageConfirmed"
    class="page-sales-funnel__information-box page-sales-funnel__information-box--error"
  >
    <div class="information-box__wrapper">

      <div class="information-box__inner">
        <h5 class="information-box__title">
          {{ $t('You already have a {name} of subscription', { name: dataStore.selectedSubscription.user_label })
          }}</h5>

        <div class="information-box__description">
          {{ $t('Copy how this all will affect his current product.') }}
        </div>

        <div style="display: flex; gap: 2rem">
          <a
            class="information-box__link"
            @click="state.isAlreadyHaveSubscriptionMessageConfirmed = true"
          >
            {{ $t('I know what I’m doing') }}
          </a>
        </div>
      </div>
    </div>
  </div>

  <FunnelStep
    :id="subscriptionStep.order"
    :details="$t('Subscription')"
    :is-active="subscriptionStep.isActive"
    :is-completed="subscriptionStep.isCompleted"
    :name="dataStore.selectedSubscription.user_label"
    @on-change-clicked="dataStore.setSelectedSubscription('')"
  >
  </FunnelStep>

  <FunnelStepLogin
    :id="accountStep.order"
    :details="$t('Account')"
    :is-active="accountStep.isActive"
    :is-completed="accountStep.isCompleted"
    :name="userStore.currentEmail || state.email"
    @on-click-complete-step="handleCompeteStep(FunnelStepId.ACCOUNT)"
    @on-change-clicked="handleChangeStep(FunnelStepId.ACCOUNT)"
  />

  <FunnelPaymentFrequency
    v-if="!paymentFrequencyStep.isHidden"
    :id="paymentFrequencyStep.order"
    :is-active="paymentFrequencyStep.isActive"
    :is-completed="paymentFrequencyStep.isCompleted"
    @on-change-clicked="handleFrequencyStepChange"
    @on-click-complete-step="handleCompeteStep(FunnelStepId.PAYMENT_FREQUENCY)"
  />

  <FunnelStepShipping
    v-if="!deliveryMethodStep.isHidden"
    :id="deliveryMethodStep.order"
    :is-active="deliveryMethodStep.isActive"
    :is-completed="deliveryMethodStep.isCompleted"
    :address="state.address"
    :is-address-editing="state.isAddressEditing"
    :allow-delivery-selection="settingsStore.isPageCZ"
    @on-click-complete-step="handleCompeteStep(FunnelStepId.DELIVERY_METHOD)"
    @on-change-clicked="handleChangeStep(FunnelStepId.DELIVERY_METHOD)"
  />

  <FunnelStep
    v-if="!addressStep.isHidden"
    :id="addressStep.order"
    :name="addressStore.addressShort"
    :details="$t('Shipping address')"
    :is-active="addressStep.isActive"
    :is-completed="addressStep.isCompleted"
    @on-change-clicked="handleChangeStep(FunnelStepId.ADDRESS)"
  >
    <FunnellAddress
      ref="funnelAddress"
      v-show="addressEditMode"
      v-model="state.address"
      :submit-label="$t('Continue to payment method')"
      @on-successful-update="() => { state.isAddressEditing = false; handleCompeteStep(FunnelStepId.ADDRESS) }"
    />

    <FunnelAddressSaved
      v-show="!addressEditMode"
      :address="state.address"
      :on-continue-click="() => handleCompeteStep(FunnelStepId.ADDRESS)"
      :on-edit-click="() => state.isAddressEditing = true"
    />
  </FunnelStep>

  <FunnelStep
    :id="paymentMethodStep.order"
    :name="paymentGatewayStore.getGatewayByCode(state.paymentGateway)?.name || ''"
    :details="$t('Payment method')"
    :is-active="paymentMethodStep.isActive"
    :is-completed="paymentMethodStep.isCompleted"
    @on-change-clicked="handleChangeStep(FunnelStepId.PAYMENT_METHOD)"
  >
    <div class="step-payment-method__form">
      <PaymentMethodRadioGroup
        v-if="!voucherStore.freeVoucher"
        v-model:paymentGateway="state.paymentGateway"
      />

      <div class="input-group checkbox-group">
        <div class="checkbox checkbox--medium">
          <input
            id="paymentCheckbox1"
            v-model="state.isPromoCodeVisible"
            :disabled="voucherStore.voucher"
            class="checkbox__input"
            data-content="paymentContent1"
            type="checkbox"
          />
          <span class="checkbox__indicator"></span>
        </div>

        <div id="paymentContent1" class="input-group__label-wrapper active">
          <label class="checkbox__label" for="paymentCheckbox1">{{ $t('I have promo code') }}</label>
          <p class="input-group__description">{{ $t('You can use one promo code per order') }}</p>

          <div
            id="paymentContent1"
            :class="{
              'active': state.isPromoCodeVisible
            }"
            class="input-group__content-wrapper"
          >
            <template v-if="voucherStore.voucher">
              <div style="display: flex;">
                <strong style="text-decoration: underline;">{{ $vVoucherCode.promoCode.$model }}
                  {{ voucherStore.discountString }}</strong>
                <div style="cursor: pointer; padding-left: 50px;" @click="voucherStore.resetVoucher">X</div>
              </div>
            </template>
            <template v-else>
              <InputText
                v-model="$vVoucherCode.promoCode.$model"
                :error="voucherStore.error"
                :placeholder="$t('Enter promo code')"
                :validator="$vVoucherCode.promoCode"
                @keyup="voucherStore.resetErrorResponse"
              />
              <button
                :class="{
                  'button button--large button--primary step-payment-method__button': true,
                  'disabled': voucherStore.isSubmitting
                }"
                @click="handleUsePromoCode"
              >
                {{ $t('Use promo code') }}
              </button>
            </template>
          </div>
        </div>
      </div>

      <div class="input-group checkbox-group">
        <div class="checkbox checkbox--medium">
          <input
            id="paymentCheckbox_2"
            v-model="state.isOrderInvoiceVisible"
            class="checkbox__input"
            data-content="paymentContent2"
            type="checkbox"
          />
          <span class="checkbox__indicator"></span>
        </div>

        <div class="input-group__label-wrapper">
          <label class="checkbox__label" for="paymentCheckbox_2">{{ $t('I need an invoice for the order') }}</label>

          <div
            id="paymentContent2"
            :class="{
              'active': state.isOrderInvoiceVisible
            }"
            class="input-group__content-wrapper"
          >

            <div v-if="!state.isInvoiceAddressSaved" class="input-group checkbox-group">
              <div class="checkbox checkbox--medium">
                <input
                  id="sameAddressCheckbox"
                  v-model="state.isShippingAddressSameAsInvoiceAddress"
                  class="checkbox__input"
                  type="checkbox"
                />
                <span class="checkbox__indicator"></span>
              </div>

              <div class="input-group__label-wrapper">
                <label class="checkbox__label" for="sameAddressCheckbox">
                  {{ $t('Billing address is the same as the shipping address') }}
                </label>
              </div>
            </div>

            <FunnelInvoiceAddress
              v-show="(!addressStore.hasInvoiceAddress && !state.isInvoiceAddressSaved) || state.isInvoiceAddressEditing"
              ref="invoiceAddress"
              v-model="state.invoiceAddress"
              :is-same-as-shipping-address="state.isShippingAddressSameAsInvoiceAddress"
              :submit-label="$t('Update billing address')"
              @on-successful-update="state.isInvoiceAddressEditing = false; state.isInvoiceAddressSaved = true"
            />

            <div
              v-show="(addressStore.hasInvoiceAddress || state.isInvoiceAddressSaved) && !state.isInvoiceAddressEditing"
              class="step-address__form"
            >
              <div class="address-details">
                <div class="address-details__item address-details__item--name">
                  <span>{{ state.invoiceAddress.first_name }} {{ state.invoiceAddress.last_name }}</span>
                  <span class="address-details__type">{{ $t('Billing address') }}</span>
                </div>
                <span class="address-details__item">{{ state.invoiceAddress.address }}</span>
                <span class="address-details__item">{{ state.invoiceAddress.zip }} {{ state.invoiceAddress.city
                  }}</span>
                <span class="address-details__item">{{ state.invoiceAddress.country }}</span>
              </div>
              <div class="address-details">
                <template v-if="state.invoiceAddress.company_name">
                  <div class="address-details__item address-details__item--name">
                    {{ state.invoiceAddress.company_name }}
                  </div>
                  <span class="address-details__item">{{ $t('Company ID') }}: {{ state.invoiceAddress.company_id
                    }}</span>
                  <span class="address-details__item">{{ $t('Tax ID') }}: {{ state.invoiceAddress.tax_id }}</span>
                  <span v-if="state.invoiceAddress.vat_id"
                        class="address-details__item">{{ $t('VAT ID') }}: {{ state.invoiceAddress.vat_id }}</span>
                </template>
                <a class="step-address__link link" @click="handleInvoiceAddressChange">
                  {{ $t('Edit billing address') }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!state.isInvoiceAddressEditing && !isPromoCodeValid" class="input-group button-group">
      <button
        class="button button--large button--primary step-payment-method__button"
        @click="handlePaymentGatewayUpdate(true)"
      >
        {{ $t('Continue to the summary without promo code') }}
      </button>
    </div>

    <div v-if="!state.isInvoiceAddressEditing && isPromoCodeValid" class="input-group button-group">
      <button
        class="button button--large button--primary step-payment-method__button"
        @click="handlePaymentGatewayUpdate(false)"
      >
        {{ $t('Continue to the summary') }}
      </button>
    </div>
  </FunnelStep>

  <FunnelStepSummary
    :id="summaryStep.order"
    :is-active="summaryStep.isActive"
    :is-completed="summaryStep.isCompleted"
  />
</template>